import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useSettings } from "@/contexts/SettingsContext";
import { useBranch } from "@/contexts/BranchContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Settings as SettingsIcon, Building2, Phone, Mail, MapPin, FileText, Save } from "lucide-react";

export default function Settings() {
  const { settings, refresh } = useSettings();
  const { branches } = useBranch();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (settings) setForm({ ...settings });
  }, [settings]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      const payload = {
        clinic_name: form.clinic_name,
        clinic_phone: form.clinic_phone,
        clinic_email: form.clinic_email,
        clinic_address: form.clinic_address,
        clinic_tagline: form.clinic_tagline,
        operation_mode: form.operation_mode,
        default_branch_id: form.default_branch_id || null,
        invoice_footer: form.invoice_footer,
        auto_send_invoice: !!form.auto_send_invoice,
      };
      await api.patch("/settings", payload);
      toast.success("Pengaturan tersimpan");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return <div className="text-sm text-muted-foreground">Memuat...</div>;

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="font-heading text-3xl tracking-tight">Pengaturan Clinic</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Identitas clinic, nomor WhatsApp utama, dan mode operasional (single/multi-branch).
        </p>
      </div>

      <form onSubmit={save} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Building2 className="h-4 w-4" /> Identitas Clinic
            </CardTitle>
            <CardDescription>Akan muncul pada invoice & notifikasi.</CardDescription>
          </CardHeader>
          <CardContent className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Nama Clinic *</Label>
              <Input data-testid="set-name-input" value={form.clinic_name || ""} onChange={(e) => setField("clinic_name", e.target.value)} required />
            </div>
            <div>
              <Label>Tagline</Label>
              <Input data-testid="set-tagline-input" value={form.clinic_tagline || ""} onChange={(e) => setField("clinic_tagline", e.target.value)} />
            </div>
            <div>
              <Label className="flex items-center gap-1"><Phone className="h-3 w-3" /> No. WhatsApp Utama Clinic</Label>
              <Input data-testid="set-phone-input" value={form.clinic_phone || ""} onChange={(e) => setField("clinic_phone", e.target.value)} placeholder="+62812..." />
              <div className="mt-1 text-xs text-muted-foreground">Format: +62 diikuti nomor (tanpa 0 di depan)</div>
            </div>
            <div>
              <Label className="flex items-center gap-1"><Mail className="h-3 w-3" /> Email Clinic</Label>
              <Input data-testid="set-email-input" type="email" value={form.clinic_email || ""} onChange={(e) => setField("clinic_email", e.target.value)} />
            </div>
            <div className="md:col-span-2">
              <Label className="flex items-center gap-1"><MapPin className="h-3 w-3" /> Alamat</Label>
              <Input data-testid="set-address-input" value={form.clinic_address || ""} onChange={(e) => setField("clinic_address", e.target.value)} />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <SettingsIcon className="h-4 w-4" /> Mode Operasional
            </CardTitle>
            <CardDescription>
              Pilih single-branch jika hanya 1 cabang. Multi-branch akan menampilkan branch switcher di header.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Mode</Label>
              <Select value={form.operation_mode || "multi_branch"} onValueChange={(v) => setField("operation_mode", v)}>
                <SelectTrigger data-testid="set-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="single_branch">Single Branch (1 cabang)</SelectItem>
                  <SelectItem value="multi_branch">Multi Branch (banyak cabang)</SelectItem>
                </SelectContent>
              </Select>
              <div className="mt-2 flex flex-wrap gap-2 text-xs">
                <Badge variant="outline" className="text-[10px]">
                  Saat ini: {form.operation_mode === "single_branch" ? "Single" : "Multi"} ({branches.length} cabang aktif)
                </Badge>
              </div>
            </div>
            {form.operation_mode === "single_branch" && (
              <div>
                <Label>Cabang Default (Single)</Label>
                <Select value={form.default_branch_id || ""} onValueChange={(v) => setField("default_branch_id", v)}>
                  <SelectTrigger data-testid="set-default-branch-select"><SelectValue placeholder="Pilih cabang" /></SelectTrigger>
                  <SelectContent>
                    {branches.map((b) => (
                      <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <FileText className="h-4 w-4" /> Template Invoice
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Footer Invoice (di-pesan WA & email customer)</Label>
              <Input data-testid="set-footer-input" value={form.invoice_footer || ""} onChange={(e) => setField("invoice_footer", e.target.value)} />
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                data-testid="set-auto-invoice"
                checked={!!form.auto_send_invoice}
                onChange={(e) => setField("auto_send_invoice", e.target.checked)}
              />
              <span className="text-sm">Auto-buka prompt kirim invoice WA setelah transaksi POS</span>
            </label>
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button type="submit" data-testid="set-save-btn" disabled={saving}>
            <Save className="mr-1.5 h-4 w-4" />
            {saving ? "Menyimpan..." : "Simpan Pengaturan"}
          </Button>
        </div>
      </form>
    </div>
  );
}
